import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  approveAndApplyAiProposal,
  decideAiProposal,
  readAiProposal,
  type AiProposal,
} from '@/api/assistant';
import { useAuth } from '@/auth/useAuth';
import {
  ApprovalGate,
  Button,
  DRAFT_ATTESTATION,
  DraftBanner,
  isConflictError,
  type DraftReview,
} from '@/ui';
import { roleLabel } from '@/ui/vocabulary';
import { formatInZone } from '@/ui/zonedTime';

type Decision = 'approve-and-apply' | 'reject';

interface Viewer {
  readonly id: string;
  readonly displayName: string;
  readonly timeZone: string;
}

/** The decision taken so far, in the words the banner states it in; null while it is still open. */
function reviewOf(proposal: AiProposal, viewer: Viewer, rejectedHere: boolean): DraftReview | null {
  switch (proposal.status) {
    case 'APPROVED':
    case 'APPLIED':
      return {
        decision: 'APPROVED',
        reviewer: proposal.approverId === viewer.id ? viewer.displayName : 'a team member',
        ...(proposal.approvedAt === undefined
          ? {}
          : {
              decidedAt: proposal.approvedAt,
              decidedAtText: formatInZone(proposal.approvedAt, viewer.timeZone),
            }),
      };
    case 'REJECTED':
      return {
        decision: 'REJECTED',
        reviewer: rejectedHere ? viewer.displayName : 'a team member',
      };
    default:
      return null;
  }
}

/**
 * An internal note the assistant drafted, waiting for a person to decide.
 *
 * The note is shown exactly as it will be saved, under the draft label, with the sentence the
 * person attests to beside the button. Approving is one deliberate click that also saves the note,
 * and it is one request: the server saves the note or leaves the draft waiting, never half of each.
 * When the customer's record has changed since the draft was written the server refuses, and the
 * screen says so and offers a new draft rather than applying a note that is out of date.
 *
 * @param props the id of the proposal, and how to ask for a new draft when this one is out of date
 * @returns the draft, its decision state and the controls that fit it
 */
export function AssistantProposal({
  id,
  onNewDraft,
}: {
  id: string;
  /** Asks the assistant for a fresh draft; absent when this person cannot ask for one. */
  onNewDraft?: () => void;
}) {
  const { user, identity, can } = useAuth();
  const client = useQueryClient();
  const key = ['assistant', identity, 'proposal', id];
  const query = useQuery({ queryKey: key, queryFn: () => readAiProposal(id) });
  const mutation = useMutation({
    mutationFn: async (decision: Decision) => {
      if (!query.data) throw new Error('Load the proposal before deciding.');
      return decision === 'approve-and-apply'
        ? approveAndApplyAiProposal(query.data)
        : decideAiProposal(query.data, false);
    },
    onSuccess: async (data) => {
      client.setQueryData(key, data);
      await Promise.all([
        client.invalidateQueries({ queryKey: ['customers'] }),
        // The open customer record is cached under its own key; without this
        // the applied note stays invisible on the page the user is looking at.
        client.invalidateQueries({ queryKey: ['customer', data.customerId] }),
      ]);
    },
    // A refused decision usually means the proposal moved on (decided elsewhere, expired) or the
    // record it was written from did; read it again so the screen shows where it stands now.
    onError: async () => {
      await client.invalidateQueries({ queryKey: key });
    },
  });
  const proposal = query.data;
  if (!proposal || !user) {
    const loadError = query.error ?? mutation.error;
    return <p role="status">{loadError?.message ?? 'Loading proposed summary…'}</p>;
  }
  // The server's words for a conflict are about editing a form, so they are never shown here.
  const conflicted = mutation.isError && isConflictError(mutation.error);
  const outOfDate = conflicted && proposal.status === 'PROPOSED';
  const error = conflicted ? query.error : (query.error ?? mutation.error);
  const payload: unknown = JSON.parse(proposal.payload);
  const body =
    typeof payload === 'object' && payload !== null && 'body' in payload
      ? String(payload.body)
      : 'Invalid proposal';
  const responsible = user.roles.includes('OWNER') || user.roles.includes(proposal.responsibleRole);
  const blocked = mutation.isPending
    ? 'Saving your decision…'
    : !can('AI_ACTION_APPROVE') || !responsible
      ? 'The responsible human role must review this proposal.'
      : undefined;
  const review = reviewOf(
    proposal,
    { id: user.id, displayName: user.displayName, timeZone: user.workspaceTimeZone },
    mutation.isSuccess && mutation.variables === 'reject',
  );
  const role = roleLabel(proposal.responsibleRole);
  const newDraft =
    onNewDraft === undefined ? null : (
      <Button variant="primary" onClick={onNewDraft}>
        Ask for a new draft
      </Button>
    );
  return (
    <section aria-label="Proposed internal summary">
      <DraftBanner subject="Internal customer summary" {...(review === null ? {} : { review })} />
      <p>Save this exact internal note to the customer’s timeline:</p>
      <pre className="assistant-panel__payload">{body}</pre>
      {error && <p role="alert">{error.message}</p>}
      {outOfDate && (
        <div role="alert">
          <p>The customer’s record changed after this draft was written, so nothing was saved.</p>
          {newDraft}
        </div>
      )}
      {proposal.status === 'PROPOSED' && !outOfDate && (
        <ApprovalGate
          approveLabel="Approve and apply"
          requireAttestation={false}
          statement={`${DRAFT_ATTESTATION} Approving takes responsibility for it as ${role}.`}
          action={body}
          consequence="The note is saved to the customer’s timeline as soon as you approve it."
          approver={user.displayName}
          approverRole={role}
          {...(blocked ? { blockedReason: blocked } : {})}
          onApprove={() => {
            mutation.mutate('approve-and-apply');
          }}
          cancelLabel="Reject proposal"
          cancelDisabled={blocked !== undefined}
          onCancel={() => {
            mutation.mutate('reject');
          }}
        />
      )}
      {proposal.status === 'APPROVED' && (
        <>
          <p role="status">This draft was approved earlier but the note was never saved.</p>
          {newDraft}
        </>
      )}
      {proposal.status === 'APPLIED' && (
        <p role="status">Internal summary saved with your approval recorded.</p>
      )}
      {proposal.status === 'REJECTED' && (
        <p role="status">Proposal rejected. No note was created.</p>
      )}
    </section>
  );
}
